import React from 'react';
import { Button, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { Check, Close, Save } from '@mui/icons-material';
import classes from '../docFwb.module.css';

const FwbInlineDiffViewer = ({
  activeDiff,
  onDecisionChange,
  onAcceptAll,
  onRejectAll,
  onSave,
  onCancel,
}) => {
  if (!activeDiff) return null;

  const {
    elements = [],
    hunks = [],
    hunkDecisions = {},
    addedCount = 0,
    removedCount = 0,
  } = activeDiff;

  const totalHunks = hunks.length;
  const acceptedCount = hunks.filter((h) => (hunkDecisions[h.id] || h.decision) === 'accepted').length;
  const rejectedCount = hunks.filter((h) => (hunkDecisions[h.id] || h.decision) === 'rejected').length;
  const pendingCount = totalHunks - acceptedCount - rejectedCount;

  return (
    <div className={classes.fwbDiffContainer}>
      <div className={classes.fwbDiffHeaderBar}>
        <div className={classes.fwbDiffHeaderTitleGroup}>
          <Typography variant='subtitle2' fontWeight={700} color='primary.main' fontSize='13px'>
            Suggested FWB Changes
          </Typography>
          <div className={classes.diffBadgeGroup}>
            <span className={classes.diffBadgeAdded}>+{addedCount}</span>
            <span className={classes.diffBadgeRemoved}>-{removedCount}</span>
          </div>
          <Typography variant='caption' color='text.secondary' fontSize='11.5px' sx={{ ml: 1 }}>
            {`${totalHunks} changes (${acceptedCount} accepted, ${rejectedCount} rejected${pendingCount > 0 ? `, ${pendingCount} pending` : ''})`}
          </Typography>
        </div>

        <Stack direction='row' spacing={1} alignItems='center'>
          <Button
            size='small'
            variant='outlined'
            color='inherit'
            onClick={onRejectAll}
            startIcon={<Close sx={{ fontSize: 14 }} />}
            sx={{
              textTransform: 'none',
              fontSize: '11.5px',
              py: '2px',
              px: 1.25,
              minHeight: '26px',
              color: '#dc2626',
              borderColor: '#fca5a5',
            }}
          >
            Reject All
          </Button>
          <Button
            size='small'
            variant='outlined'
            color='primary'
            onClick={onAcceptAll}
            startIcon={<Check sx={{ fontSize: 14 }} />}
            sx={{
              textTransform: 'none',
              fontSize: '11.5px',
              py: '2px',
              px: 1.25,
              minHeight: '26px',
            }}
          >
            Accept All
          </Button>
          <Button
            size='small'
            variant='contained'
            color='primary'
            onClick={onSave}
            disabled={acceptedCount === 0}
            startIcon={<Save sx={{ fontSize: 14 }} />}
            sx={{
              textTransform: 'none',
              fontSize: '11.5px',
              py: '2px',
              px: 1.5,
              minHeight: '26px',
              fontWeight: 700,
            }}
          >
            Save Changes {acceptedCount > 0 ? `(${acceptedCount})` : ''}
          </Button>
          <Tooltip title='Discard all changes and exit review'>
            <IconButton size='small' onClick={onCancel} sx={{ ml: 0.5 }}>
              <Close sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </div>

      <div className={classes.fwbDiffBody} role='table' aria-label='Suggested FWB Changes Diff'>
        {elements.map((el, elIdx) => {
          if (el.type === 'unchanged') {
            return (
              <div key={`unchanged-${elIdx}-${el.oldLineNumber}`} className={classes.fwbDiffRowUnchanged} role='row'>
                <span className={classes.fwbDiffLineNum}>{el.oldLineNumber || el.newLineNumber || ''}</span>
                <span className={classes.fwbDiffPrefix}> </span>
                <span className={classes.fwbDiffText}>{el.line}</span>
              </div>
            );
          }

          if (el.type === 'hunk') {
            const hunk = el.hunk;
            const decision = hunkDecisions[hunk.id] !== undefined ? hunkDecisions[hunk.id] : (hunk.decision || 'pending');

            let cardClass = classes.fwbDiffHunkCard;
            let toolbarClass = classes.fwbDiffHunkToolbar;
            if (decision === 'accepted') {
              cardClass += ` ${classes.fwbDiffHunkAccepted}`;
              toolbarClass += ` ${classes.fwbDiffHunkToolbarAccepted}`;
            } else if (decision === 'rejected') {
              cardClass += ` ${classes.fwbDiffHunkRejected}`;
              toolbarClass += ` ${classes.fwbDiffHunkToolbarRejected}`;
            }

            return (
              <div key={hunk.id} className={cardClass} role='region' aria-label={`Change hunk at line ${hunk.oldStartLine}`}>
                {hunk.oldLines.map((oldItem, oIdx) => {
                  let rowClass = classes.fwbDiffRowRemoved;
                  if (decision === 'rejected') rowClass += ` ${classes.fwbDiffRowRemovedRejected}`;
                  else if (decision === 'accepted') rowClass += ` ${classes.fwbDiffRowRemovedAccepted}`;

                  return (
                    <div key={`rem-${hunk.id}-${oIdx}`} className={rowClass}>
                      <span className={classes.fwbDiffLineNum}>{oldItem.lineNumber || ''}</span>
                      <span className={classes.fwbDiffPrefix}>{decision === 'rejected' ? ' ' : '-'}</span>
                      <span className={classes.fwbDiffText}>{oldItem.line}</span>
                    </div>
                  );
                })}

                {hunk.newLines.map((newItem, nIdx) => {
                  let rowClass = classes.fwbDiffRowAdded;
                  if (decision === 'rejected') rowClass += ` ${classes.fwbDiffRowAddedRejected}`;

                  return (
                    <div key={`add-${hunk.id}-${nIdx}`} className={rowClass}>
                      <span className={classes.fwbDiffLineNum}>{newItem.lineNumber || ''}</span>
                      <span className={classes.fwbDiffPrefix}>+</span>
                      <span className={classes.fwbDiffText}>{newItem.line}</span>
                    </div>
                  );
                })}

                <div className={toolbarClass}>
                  <Stack direction='row' spacing={1} alignItems='center' sx={{ ml: 'auto' }}>
                    {decision === 'accepted' ? (
                      <>
                        <Chip size='small' label='✓ Accepted' color='success' sx={{
                          height: '22px',
                          fontSize: '11px',
                          fontWeight: 700 
                        }} />
                        <Button size='small' variant='text' onClick={() => onDecisionChange(hunk.id, 'pending')} sx={{
                          minWidth: 'auto',
                          py: '1px',
                          px: 1,
                          fontSize: '11px',
                          textTransform: 'none',
                          color: '#64748b' 
                        }}>
                            Undo
                        </Button>
                      </>
                    ) : decision === 'rejected' ? (
                      <>
                        <Chip size='small' label='✕ Rejected' color='default' sx={{
                          height: '22px',
                          fontSize: '11px',
                          fontWeight: 700,
                          color: '#b91c1c',
                          backgroundColor: '#fee2e2' 
                        }} />
                        <Button size='small' variant='text' onClick={() => onDecisionChange(hunk.id, 'pending')} sx={{
                          minWidth: 'auto',
                          py: '1px',
                          px: 1,
                          fontSize: '11px',
                          textTransform: 'none',
                          color: '#64748b' 
                        }}>
                            Undo
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button size='small' variant='outlined' color='inherit' onClick={() => onDecisionChange(hunk.id, 'rejected')} startIcon={<Close sx={{ fontSize: 13 }} />} sx={{
                          textTransform: 'none',
                          fontSize: '11px',
                          py: '1px',
                          px: 1,
                          minHeight: '22px',
                          color: '#dc2626',
                          borderColor: '#fca5a5' 
                        }}>
                            Reject
                        </Button>
                        <Button size='small' variant='contained' color='success' onClick={() => onDecisionChange(hunk.id, 'accepted')} startIcon={<Check sx={{ fontSize: 13 }} />} sx={{
                          textTransform: 'none',
                          fontSize: '11px',
                          py: '1px',
                          px: 1,
                          minHeight: '22px',
                          fontWeight: 700 
                        }}>
                            Accept
                        </Button>
                      </>
                    )}
                  </Stack>
                </div>
              </div>
            );
          }
          return null;
        })}
      </div>
    </div>
  );
};

export default FwbInlineDiffViewer;
